import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { learnerStatusApi } from '@/api/learnerStatus'
import { MemberStatusBadge } from './MemberStatusBadge'
import { formatKstClockTime } from '@/utils/time'

/** 오늘 등하원 시각 — 서울 기준 HH:mm. 값이 없으면 null(요약 줄에서 생략). */
function formatTime(iso: string | null): string | null {
  return formatKstClockTime(iso) || null
}

/**
 * 원생 상세(수정 모달) 상단 — 파생 상태 + 오늘 출결 요약 + 휴원 기간/사유.
 * enrollment 0건(404)·로딩·에러는 섹션 숨김(fail-soft).
 */
export function LearnerStatusSummarySection({ userId }: { userId: number }) {
  const { t, i18n } = useTranslation(['admin'])
  const { data, isError, isLoading } = useQuery({
    queryKey: ['lc', 'learnerStatusSummary', userId],
    queryFn: async () => (await learnerStatusApi.getSummary(userId)).data.data,
    retry: false,
  })

  if (isLoading || isError || !data) return null

  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'
  const attendanceLabel = data.today.attendanceStatus
    ? t(`admin:students.statusSummary.attendance.${data.today.attendanceStatus}`, {
        defaultValue: data.today.attendanceStatus,
      })
    : t('admin:students.statusSummary.attendance.none')
  const times = [formatTime(data.today.checkInAt), formatTime(data.today.checkOutAt)]
    .filter(Boolean)
    .join(' ~ ')

  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{t('admin:students.statusSummary.title')}</span>
        <MemberStatusBadge status={data.status} />
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('admin:students.statusSummary.todayAttendance')}</span>
        <span className="text-gray-800">
          {attendanceLabel}
          {times ? ` (${times})` : ''}
        </span>
      </div>
      {data.status === 'ON_LEAVE' && data.leaveStart && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{t('admin:students.statusSummary.leavePeriod')}</span>
          <span className="text-gray-800">
            {data.leaveStart}
            {' ~ '}
            {data.leaveEnd ?? ''}
          </span>
        </div>
      )}
      {data.statusReason && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{t('admin:students.statusSummary.reason')}</span>
          <span className="text-gray-800">{data.statusReason}</span>
        </div>
      )}
      {data.statusChangedAt && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{t('admin:students.statusSummary.changedAt')}</span>
          <span className="text-gray-800">{new Date(data.statusChangedAt).toLocaleDateString(locale)}</span>
        </div>
      )}
    </div>
  )
}
